// Pure helpers for Individual Goals. They only describe what the goals API returned; no score,
// progress or target is invented. Statuses are the server's IndividualGoalStatus values.

export const GOAL_GROUPS = ["PROPOSED", "ACTIVE", "COMPLETED", "CLOSED"];
const APPROVED = ["ACTIVE", "COMPLETED"];

export const goalStatus = goal => String(goal?.status || "").toUpperCase();
export const goalGroup = goal => {
    const status = goalStatus(goal);
    return status === "REJECTED" || status === "CANCELLED" ? "CLOSED" : status;
};

export function groupGoals(goals = []) {
    return GOAL_GROUPS.map(id => ({ id, goals: goals.filter(g => goalGroup(g) === id) })).filter(group => group.goals.length > 0);
}

// Weight share among the period's approved goals (the goals the Individual Goals score draws on).
export function relativeWeight(goal, goals = []) {
    if (!APPROVED.includes(goalStatus(goal))) return null;
    const total = goals.filter(g => APPROVED.includes(goalStatus(g))).reduce((sum, g) => sum + Math.max(0, Number(g.weight) || 0), 0);
    return total > 0 ? (100 * Math.max(0, Number(goal.weight) || 0)) / total : null;
}

export function summarizeGoals(goals = []) {
    const approved = goals.filter(g => APPROVED.includes(goalStatus(g)));
    const weightSum = approved.reduce((sum, g) => sum + Math.max(0, Number(g.weight) || 0), 0);
    const progressWeight = approved.reduce((sum, g) => sum + Math.max(0, Number(g.weight) || 0) * Math.min(100, Math.max(0, Number(g.progress) || 0)), 0);
    return {
        total: goals.length,
        approved: approved.length,
        awaiting: goals.filter(g => goalStatus(g) === "PROPOSED").length,
        evaluated: goals.filter(g => goalStatus(g) === "COMPLETED").length,
        // Employee-reported progress, weighted by importance; null when nothing is approved yet.
        reportedProgress: weightSum > 0 ? progressWeight / weightSum : null,
    };
}

// An active goal is overdue once its due date has passed.
export function isGoalOverdue(goal, now = Date.now()) {
    if (goalStatus(goal) !== "ACTIVE" || !goal?.deadline) return false;
    const due = new Date(goal.deadline).getTime();
    return Number.isFinite(due) && due < now;
}

// The importance level whose score matches the goal's weight, when the scale is known.
export function importanceLabel(weight, levels = []) {
    const level = levels.find(l => Number(l.score) === Number(weight));
    return level ? level.label : null;
}
